import React from 'react';
import { call, fork, put, select } from 'redux-saga/effects';
import Api, { doLogout } from '@/domain/api';
import { yieldErrorNotification, yieldSuccessNotification } from '@/lib/toasts';
import { FormattedMessage } from 'react-intl';
import { addUiDeferredToastAction } from '@/domain/ui';
import { DeferredToastFactory } from '@/domain/ui/helpers';
import { executeFunc } from '@/lib/helpers';
import { rootCategoriesListSelector } from '@/domain/categories';
import { backSearchUrlSelector } from '@/domain/env';
import { findCatById, isCategoryAll } from '@/domain/categories/helpers';
import { isGridWorkSpaceSelector } from '@/domain/companies';
import * as actions from '@/domain/documents/documentsActions';
import * as selectors from '@/domain/documents/documentSelector';
import * as Categories from '@/domain/categories/sagas';
import * as AgGridActions from '@/domain/agGrid';

import {
  ensureSearchDocuments,
  ensureWorkspaceGridApplyTransaction,
  ensureWorkspaceGridRefreshServerSide,
  invalidateWorkspaceAggregations,
} from '@/domain/documents/sagas/sagas.workspace';

import Box from '@mui/material/Box';

// Helper functions
const createMessageConfig = (id, defaultMessage, values = {}) => ({
  id,
  defaultMessage,
  values,
});

function* dispatchMoveToCompanySuccessAction(movedDocumentIDs, nonLinkedDocumentIDs) {
  yield put({
    type: actions.moveDocumentsToCompanyAction.success,
    payload: { movedIDs: movedDocumentIDs, nonLinkedDocumentIDs },
  });

  yield fork(ensureWorkspaceGridApplyTransaction, {
    transactionType: 'remove',
    nodes: movedDocumentIDs,
  });
}

function* showNotificationOrDeferred(hasRedirect, messageConfig) {
  if (hasRedirect) {
    yield put({
      type: addUiDeferredToastAction.type,
      payload: DeferredToastFactory({
        message: messageConfig,
        type: 'success',
      }),
    });
  } else {
    yieldSuccessNotification(<FormattedMessage {...messageConfig} />);
  }
}

function* handleMixedMoveToCompanySuccess(data, companyName) {
  const { successArray, successBundlesArray, ignoredArray, ignoredBundlesArray } = data;

  const movedDocumentIDs = [...successArray, ...successBundlesArray.flat()];
  const documentsTotal = successArray.length + ignoredArray.length;
  const bundlesTotal = ignoredBundlesArray.length + successBundlesArray.length;

  // so in grid view count_without_link_panels - its mix ids from success and success_bundles
  yield* dispatchMoveToCompanySuccessAction(movedDocumentIDs, movedDocumentIDs);

  const notificationContent = (
    <Box>
      {documentsTotal > 0 && (
        <Box mb={1}>
          <FormattedMessage
            values={{ companyName, success: successArray.length, total: documentsTotal }}
            id="toast.moveMixedToCompany.documents"
            defaultMessage={`${successArray.length} out of ${documentsTotal} documents were moved to the ${companyName}`}
          />
        </Box>
      )}
      <Box>
        <FormattedMessage
          values={{ companyName, success: successBundlesArray.length, total: bundlesTotal }}
          id="toast.moveMixedToCompany.bundles"
          defaultMessage={`${successBundlesArray.length} out of ${bundlesTotal} linked bundles were moved to the ${companyName}`}
        />
      </Box>
    </Box>
  );

  yieldSuccessNotification(notificationContent);
}

function* handleLinkMoveToCompanySuccess(successIDs, companyName, hasRedirect) {
  yield* dispatchMoveToCompanySuccessAction(successIDs, []);

  const messageConfig = createMessageConfig('toast.moveLinkedToCompany', `Linked bundle moved to ${companyName}`, {
    companyName,
  });

  yield* showNotificationOrDeferred(hasRedirect, messageConfig);
}

function* handleRegularMoveToCompanySuccess(successIDs, companyName, selectedDocumentsCount, hasRedirect) {
  yield* dispatchMoveToCompanySuccessAction(successIDs, successIDs);

  const messageConfig = createMessageConfig(
    'toast.moveDocumentsToCompany',
    `${successIDs.length} out of ${selectedDocumentsCount} documents moved to ${companyName}`,
    {
      companyName,
      success: successIDs.length,
      total: selectedDocumentsCount,
    },
  );

  yield* showNotificationOrDeferred(hasRedirect, messageConfig);
}

function* handleApiError(err, action, reject) {
  const errorMessage = err?.response?.data?.error || err?.response?.data || '';
  if (errorMessage) {
    yieldErrorNotification(errorMessage);
  }
  executeFunc(reject, err);
  yield doLogout(action, err);
}

export function* ensureMoveDocumentsToCompany({ data, resolve, reject, hasRedirect }) {
  try {
    const { toCompanyID, documentID, companyName, selectedDocumentsCount, linkID } = data;
    const requestData = linkID ? { linkID } : { documentID };

    const { data: response } = yield call(Api.moveDocumentsToCompany, {
      data: { toCompanyID, ...requestData },
    });

    // eslint-disable-next-line camelcase
    const { success = [], ignored = [], success_bundles = [], ignored_bundles = [] } = response;

    // Handle null values from backend by providing fallback arrays
    const successArray = success || [];
    const ignoredArray = ignored || [];
    // eslint-disable-next-line camelcase
    const successBundlesArray = success_bundles || [];
    // eslint-disable-next-line camelcase
    const ignoredBundlesArray = ignored_bundles || [];
    const isMixedMove = ignoredBundlesArray.length > 0 || successBundlesArray.length > 0;

    // isMixedMove - available only from grid view
    if (isMixedMove) {
      yield* handleMixedMoveToCompanySuccess(
        {
          successArray,
          ignoredArray,
          successBundlesArray,
          ignoredBundlesArray,
        },
        companyName,
      );
    } else if (linkID) {
      yield* handleLinkMoveToCompanySuccess(successArray, companyName, hasRedirect);
    } else {
      yield* handleRegularMoveToCompanySuccess(successArray, companyName, selectedDocumentsCount, hasRedirect);
    }

    executeFunc(resolve, response);
  } catch (err) {
    yield* handleApiError(err, actions.moveDocumentsToCompanyAction, reject);
  }
}

export function* ensureMoveDocumentToCompany({ data, resolve, reject, hasRedirect = false }) {
  try {
    const { toCompanyID, documentID, companyName } = data;

    const { data: response } = yield call(Api.moveDocumentToCompany, {
      data: { documentID, toCompanyID },
    });

    yield put({
      type: actions.moveDocumentToCompanyAction.success,
      payload: { id: documentID },
    });

    yield fork(ensureWorkspaceGridApplyTransaction, {
      transactionType: 'remove',
      nodes: [documentID],
    });

    const messageConfig = createMessageConfig('toast.moveDocumentToCompany', `Document moved to ${companyName}`, {
      companyName,
    });

    yield* showNotificationOrDeferred(hasRedirect, messageConfig);

    executeFunc(resolve, response);
  } catch (err) {
    yield* handleApiError(err, actions.moveDocumentToCompanyAction, reject);
  }
}

function* refreshWorkspaceDocumentList(params, source) {
  if (source !== 'document') {
    const list = yield select(selectors.documentsByIdSelector);
    const isGrid = yield select(isGridWorkSpaceSelector);

    if (isGrid) {
      yield put({
        type: AgGridActions.agGridRefreshServerSideAction.type,
        payload: false,
      });
      invalidateWorkspaceAggregations();
    } else {
      yield call(ensureSearchDocuments, { params, force: true }, list.size);
    }
  }
}

function* showMoveToCategoryNotification(category, hasRedirect) {
  if (hasRedirect) {
    const messageConfig = {
      id: 'category.move.toast.success',
      defaultMessage: 'Document was moved to {catName}',
      values: {
        catName: {
          id: `category.name.${category.nameLangId}`,
          defaultMessage: category.name,
          style: { fontWeight: 600 },
        },
      },
    };

    yield put({
      type: addUiDeferredToastAction.type,
      payload: DeferredToastFactory({
        message: messageConfig,
        type: 'success',
      }),
    });
  } else {
    const notificationContent = (
      <FormattedMessage
        values={{
          catName: (
            <span style={{ fontWeight: 600 }}>
              <FormattedMessage id={`category.name.${category.nameLangId}`} defaultMessage={category.name} />
            </span>
          ),
        }}
        id="category.move.toast.success"
        defaultMessage="Document was moved to {catName}"
      />
    );

    yieldSuccessNotification(notificationContent);
  }
}

export function* ensureMoveDocumentToCategory({ payload, params }) {
  const {
    categoryId,
    documentID = [],
    linkedID = [],
    workspaceType,
    rootCategory: rootCategoryFromPayload,
    resolve = (x) => x,
    reject = (x) => x,
    source,
    hasRedirect,
  } = payload;

  try {
    const rootCategories = yield select(rootCategoriesListSelector);
    const category = rootCategories.find((cat) => cat.id === categoryId);
    const isConfidential = workspaceType === 'confidential';

    let rootCategory;
    if (source !== 'document') {
      rootCategory = rootCategoryFromPayload;
    } else {
      const backSearchUrl = yield select(backSearchUrlSelector);
      rootCategory = backSearchUrl?.params?.category1;
    }

    const isCurrentCategoryAll = isCategoryAll(findCatById(rootCategories, rootCategory));

    const { data } = yield call(Api.moveDocumentToCategory, {
      data: { documentID, linkedID, categoryId, isConfidential },
    });

    yield call(Categories.ensureGetCategories, {
      ...params,
      companyType: workspaceType,
    });

    if (!isCurrentCategoryAll) {
      yield put({
        type: actions.documentClear.type,
        payload: documentID,
      });
    }

    yield* refreshWorkspaceDocumentList(params, source, workspaceType);
    yield* showMoveToCategoryNotification(category, hasRedirect);

    resolve(data);
  } catch (err) {
    reject(err);
    yield doLogout(actions.documentStopProcessingAction, err);
  }
}

// COMPANY BAR PANEL - MOVE ALL DOCUMENTS TO OTHER CATEGORY
export function* ensureMoveAllCategoryDocumentsToOtherCategory({ payload }) {
  const { fromCategoryId, toCategoryId, workspaceType } = payload;

  try {
    const rootCategories = yield select(rootCategoriesListSelector);
    const category = rootCategories.find((cat) => cat.id === toCategoryId);
    const isConfidential = workspaceType === 'confidential';

    yield call(Api.moveAllCategoryDocumentsToOtherCategory, {
      params: {
        category: fromCategoryId,
        targetCategoryId: toCategoryId,
        isConfidential,
      },
    });

    yield call(Categories.ensureGetCategories, {
      companyType: workspaceType,
    });

    yield put({
      type: actions.documentClearAll.type,
      payload: null,
    });

    yield fork(ensureWorkspaceGridRefreshServerSide, true);

    const notificationContent = (
      <FormattedMessage
        values={{
          catName: (
            <span style={{ fontWeight: 600 }}>
              <FormattedMessage id={`category.name.${category.nameLangId}`} defaultMessage={category.name} />
            </span>
          ),
        }}
        id="category.move.toast.success.bulk"
        defaultMessage="All documents moved to {catName}"
      />
    );

    yieldSuccessNotification(notificationContent);
  } catch (err) {
    yield doLogout(actions.moveAllCategoryDocumentsToOtherCategoryAction, err);
  }
}

export function* ensureRestoreDocuments({ data, resolve, reject, params }) {
  try {
    const { documentIDs, categoryId, source } = data;
    const rootCategories = yield select(rootCategoriesListSelector);
    const category = rootCategories.find((cat) => cat.id === categoryId);

    const { data: response } = yield call(Api.restoreDocuments, {
      data: { document_ids: documentIDs, category_id: categoryId },
    });

    const { success = [] } = response;

    if (data.success?.length > 0) {
      yield fork(ensureWorkspaceGridApplyTransaction, {
        transactionType: 'remove',
        nodes: documentIDs,
      });
    }

    yield* refreshWorkspaceDocumentList(params, source);

    let notificationContent;
    if (categoryId) {
      notificationContent = (
        <FormattedMessage
          values={{
            catName: (
              <span style={{ fontWeight: 600 }}>
                <FormattedMessage id={`category.name.${category.nameLangId}`} defaultMessage={category.name} />
              </span>
            ),
            success: success.length,
            total: documentIDs.length,
          }}
          id="toast.restoreTo"
          defaultMessage={`${success.length} out of ${documentIDs.length} documents restored to ${category.name}`}
        />
      );
    } else {
      notificationContent = (
        <FormattedMessage
          values={{
            success: success.length,
            total: documentIDs.length,
          }}
          id="toast.restore"
          defaultMessage={`${success.length} out of ${documentIDs.length} documents restored`}
        />
      );
    }

    yieldSuccessNotification(notificationContent);
    executeFunc(resolve, response);
  } catch (err) {
    yield* handleApiError(err, actions.restoreDocumentsAction, reject);
  }
}
